/**
 * Public affiliate lander at /a/:slug
 *
 * Renders a short branded page for an affiliate link, tracks the view + click,
 * and redirects the visitor to the shop with UTM parameters attached. Auto
 * countdown (3s) with a big "Continue now" button so a decisive click is
 * always available.
 *
 * Three templates:
 *   redirect  — minimal, luxurious white bg, big CTA + countdown
 *   editorial — dark navy hero (matches domain lander editorial), for premium partners
 *   poster    — bold cream poster with oversized headline
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useBranding } from "@/lib/branding";
import { fetchPublicAffiliateLander, clickAffiliateLink } from "@/lib/api";

const REDIRECT_SECONDS = 3;

export default function AffiliateLander() {
  const { slug } = useParams();
  const brand = useBranding();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);
  const [redirecting, setRedirecting] = useState(false);
  const destinationRef = useRef(null);
  const redirectTimer = useRef(null);

  // Fetch the public offer data
  useEffect(() => {
    (async () => {
      try {
        const d = await fetchPublicAffiliateLander(slug);
        setData(d);
      } catch {
        setNotFound(true);
      }
    })();
  }, [slug]);

  // Preload destination URL (POST /click) as soon as we have the offer.
  // We do NOT redirect yet — countdown ticks so the user sees the message.
  useEffect(() => {
    if (!data) return;
    (async () => {
      try {
        const r = await clickAffiliateLink(slug);
        destinationRef.current = r?.destination || null;
      } catch {
        destinationRef.current = null;
      }
    })();
  }, [data, slug]);

  // Countdown → auto-redirect
  useEffect(() => {
    if (!data || redirecting) return;
    if (countdown <= 0) {
      go();
      return;
    }
    redirectTimer.current = setTimeout(() => setCountdown((n) => n - 1), 1000);
    return () => clearTimeout(redirectTimer.current);
  }, [data, countdown, redirecting]);

  const go = () => {
    if (redirecting) return;
    setRedirecting(true);
    const dest = destinationRef.current;
    if (dest) {
      window.location.href = dest;
    } else {
      // Fallback: refresh click endpoint on-demand
      clickAffiliateLink(slug)
        .then((r) => { if (r?.destination) window.location.href = r.destination; })
        .catch(() => setRedirecting(false));
    }
  };

  const accent = data?.accent_color?.trim() || "#03142f";
  const template = useMemo(() => (data?.template || "redirect"), [data]);

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white font-sans" data-testid="aff-not-found">
        <div className="text-center">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#666]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>404</div>
          <div className="mt-3 text-2xl" style={{ fontFamily: "'Instrument Serif', serif" }}>This link is no longer active.</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center p-10 font-mono text-sm text-[#525252]">Loading…</div>;
  }

  const heading = data.heading || `Continue to ${data.shop}`;
  const subheading = data.subheading || `You're being taken to ${data.shop} — offers curated by ${brand?.brand_name || "CasperDomains"}.`;
  const ctaLabel = data.cta_label || `Continue to ${data.shop} →`;

  if (template === "editorial") {
    return (
      <div
        className="min-h-screen text-white flex flex-col antialiased"
        style={{ backgroundColor: accent, animation: "aff-fade 500ms ease-out both", fontFamily: "'Fraunces', 'IBM Plex Sans', sans-serif" }}
        data-testid="aff-lander"
      >
        <Header brand={brand} slug={slug} tone="dark" />
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="max-w-3xl text-center">
            <div className="text-[11px] uppercase tracking-[0.32em] text-white/60 mb-8"
                 style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              Affiliate partner · {data.category}
            </div>
            <h1 data-testid="aff-heading"
                className="leading-[0.92] tracking-tight"
                style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 500,
                         fontSize: "clamp(2.75rem, 7vw, 5.5rem)" }}>
              {heading}
            </h1>
            <p className="mt-8 max-w-2xl mx-auto text-white/80 text-lg italic"
               style={{ fontFamily: "'Instrument Serif', serif" }}>
              {subheading}
            </p>
            <div className="mt-12">
              <ContinueButton onClick={go} label={ctaLabel} tone="dark" />
              <Countdown n={countdown} redirecting={redirecting} tone="dark" />
            </div>
          </div>
        </div>
        <Footer brand={brand} tone="dark" />
        <style>{`@keyframes aff-fade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }`}</style>
      </div>
    );
  }

  if (template === "poster") {
    return (
      <div
        className="min-h-screen flex flex-col antialiased"
        style={{ backgroundColor: "#F1EBDD", animation: "aff-fade 500ms ease-out both" }}
        data-testid="aff-lander"
      >
        <Header brand={brand} slug={slug} tone="light" />
        <div className="flex-1 flex items-center justify-center px-6 py-12 text-center">
          <div className="max-w-5xl">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#8A6D3B] mb-6"
                 style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              Curated partner · {data.shop}
            </div>
            <h1 data-testid="aff-heading"
                className="leading-[0.85] tracking-tight break-words"
                style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700,
                         fontSize: "clamp(3.5rem, 12vw, 9rem)", color: "#111111" }}>
              {heading}
            </h1>
            <p className="mt-8 max-w-2xl mx-auto text-[#5A4B2A] italic"
               style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.15rem, 1.8vw, 1.5rem)" }}>
              {subheading}
            </p>
            <div className="mt-12">
              <ContinueButton onClick={go} label={ctaLabel} tone="light" accent={accent} />
              <Countdown n={countdown} redirecting={redirecting} tone="light" />
            </div>
          </div>
        </div>
        <Footer brand={brand} tone="light" />
        <style>{`@keyframes aff-fade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }`}</style>
      </div>
    );
  }

  // Default: "redirect" — minimal, universal, works for every partner.
  return (
    <div
      className="min-h-screen bg-white flex flex-col antialiased"
      style={{ animation: "aff-fade 500ms ease-out both" }}
      data-testid="aff-lander"
    >
      <Header brand={brand} slug={slug} tone="light" />
      <div className="flex-1 flex items-center justify-center px-6 py-12 text-center">
        <div className="max-w-2xl">
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#525252] mb-8"
               style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            Partner offer · {data.shop}
          </div>
          <h1 data-testid="aff-heading"
              className="leading-[0.95] tracking-tight text-[#101828]"
              style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400,
                       fontSize: "clamp(2.5rem, 6vw, 4.5rem)" }}>
            {heading}
          </h1>
          <p className="mt-6 max-w-lg mx-auto text-[#525252] leading-relaxed">
            {subheading}
          </p>
          <div className="mt-10">
            <ContinueButton onClick={go} label={ctaLabel} tone="light" accent={accent} />
            <Countdown n={countdown} redirecting={redirecting} tone="light" />
          </div>
        </div>
      </div>
      <Footer brand={brand} tone="light" />
      <style>{`@keyframes aff-fade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }`}</style>
    </div>
  );
}

function Header({ brand, slug, tone }) {
  const dark = tone === "dark";
  return (
    <header className={`border-b ${dark ? "border-white/15" : "border-[#eaecf0]"}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
        <span className={`text-[15px] tracking-tight ${dark ? "text-white" : "text-[#101828]"}`}
              style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 500 }}>
          {brand?.brand_name || "CasperDomains"}
        </span>
        <span className={`text-[10px] uppercase tracking-[0.24em] ${dark ? "text-white/60" : "text-[#666]"}`}
              style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          Partner · /a/{slug}
        </span>
      </div>
    </header>
  );
}

function ContinueButton({ onClick, label, tone, accent }) {
  const dark = tone === "dark";
  const bg = dark ? "bg-white text-[#101828]" : "";
  const style = dark ? undefined : { backgroundColor: accent || "#03142f", color: "#ffffff" };
  return (
    <button
      onClick={onClick}
      data-testid="aff-continue"
      className={`inline-flex items-center gap-2 px-8 h-12 rounded-sm text-[11px] uppercase tracking-[0.28em] transition-transform hover:-translate-y-[1px] ${bg}`}
      style={{ fontFamily: "'JetBrains Mono', monospace", ...(style || {}) }}
    >
      <span>{label}</span>
      <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
    </button>
  );
}

function Countdown({ n, redirecting, tone }) {
  const color = tone === "dark" ? "text-white/50" : "text-[#999]";
  return (
    <div
      data-testid="aff-countdown"
      className={`mt-6 text-[10px] uppercase tracking-[0.28em] ${color}`}
      style={{ fontFamily: "'JetBrains Mono', monospace" }}
    >
      {redirecting ? "Redirecting…" : `Auto-continue in ${n}s`}
    </div>
  );
}

function Footer({ brand, tone }) {
  const dark = tone === "dark";
  return (
    <footer className={`border-t ${dark ? "border-white/15" : "border-[#eaecf0]"} py-6`}>
      <div className={`max-w-7xl mx-auto px-6 md:px-12 text-[10px] uppercase tracking-[0.24em] ${dark ? "text-white/50" : "text-[#666]"} flex justify-between`}
           style={{ fontFamily: "'JetBrains Mono', monospace" }}>
        <span>© {brand?.brand_name || "CasperDomains"}</span>
        <span>Ad · sponsored partner</span>
      </div>
    </footer>
  );
}
